import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { HiOutlineTrash, HiOutlinePlus, HiOutlineMinus, HiOutlineArrowLeft } from "react-icons/hi2";
import { TbTruckDelivery, TbDiscount } from "react-icons/tb";
import { RiShieldCheckLine, RiShoppingBag3Line } from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";

// Import your product images (Mocking 2 items for display)
import productimg1 from "../assets/productimg1.png";
import productimg4 from "../assets/productimg4.png";

const Cart = () => {
  // Mock Cart State (Usually this comes from your Context/Redux)
  const [cartItems, setCartItems] = useState([
    {
      id: 1,
      name: "Ultra-Thin Bamboo Day Pads",
      price: 5.99,
      quantity: 2,
      image: productimg1,
      tag: "Bamboo"
    },
    {
      id: 2,
      name: "Organic Cotton Regular Flow",
      price: 6.50,
      quantity: 1,
      image: productimg4,
      tag: "Organic"
    }
  ]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const updateQuantity = (id, delta) => {
    setCartItems(prev => prev.map(item => 
      item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
    ));
  };

  const removeItem = (id, name) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
    toast.error(`${name} removed from box`, {
      icon: '🗑️',
      style: { borderRadius: '15px', background: '#fff', color: '#9f1239', fontWeight: 'bold' }
    });
  };

  const subtotal = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const shipping = subtotal > 20 ? 0 : 5.00;
  const total = subtotal + shipping;

  if (cartItems.length === 0) return <EmptyCart />;

  return (
    <div className="min-h-screen bg-[#FFF0F3] py-20 px-6">
      <Toaster position="bottom-center" />
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-black text-rose-950 tracking-tighter uppercase">Your Care Box</h1>
            <p className="text-rose-900/40 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">
              Review your monthly essentials
            </p>
          </div>
          <Link to="/products" className="flex items-center gap-2 text-rose-600 font-bold hover:gap-3 transition-all">
            <HiOutlineArrowLeft /> Keep Shopping
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* CART ITEMS LIST */}
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence>
              {cartItems.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  className="bg-white/60 backdrop-blur-xl border border-white rounded-[2.5rem] p-6 flex flex-col md:flex-row items-center gap-6 shadow-xl shadow-rose-100/50"
                >
                  {/* Image */}
                  <div className="w-32 h-32 bg-rose-50 rounded-3xl overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>

                  {/* Details */}
                  <div className="flex-1 text-center md:text-left">
                    <span className="text-[10px] font-black text-rose-400 uppercase tracking-widest bg-rose-50 px-3 py-1 rounded-full mb-2 inline-block">
                      {item.tag}
                    </span>
                    <h3 className="text-xl font-bold text-rose-950">{item.name}</h3>
                    <p className="text-rose-500 font-black text-lg mt-1">₹{item.price}</p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-4 bg-white rounded-2xl p-2 border border-rose-50">
                    <button 
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-10 h-10 flex items-center justify-center text-rose-400 hover:text-rose-600 transition-colors"
                    >
                      <HiOutlineMinus />
                    </button>
                    <span className="font-black text-rose-950 w-6 text-center">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-10 h-10 flex items-center justify-center text-rose-400 hover:text-rose-600 transition-colors"
                    >
                      <HiOutlinePlus />
                    </button>
                  </div>

                  {/* Remove Button */}
                  <button 
                    onClick={() => removeItem(item.id, item.name)}
                    className="p-4 text-rose-200 hover:text-rose-600 transition-colors"
                  >
                    <HiOutlineTrash size={24} />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* ORDER SUMMARY */}
          <div className="lg:col-span-1">
            <div className="bg-rose-950 rounded-[3rem] p-8 text-white shadow-2xl sticky top-32">
              <h2 className="text-2xl font-black tracking-tighter uppercase mb-8 border-b border-white/10 pb-4">Summary</h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-rose-200/60 font-medium">
                  <span>Subtotal</span>
                  <span className="text-white">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-rose-200/60 font-medium">
                  <span>Shipping</span>
                  <span className="text-white">{shipping === 0 ? "FREE" : `₹${shipping.toFixed(2)}`}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-[10px] text-rose-400 font-bold uppercase tracking-wider">
                    Add ₹{(20 - subtotal).toFixed(2)} more for FREE shipping
                  </p>
                )}
              </div>

              {/* Promo Code */}
              <div className="relative mb-8">
                <input 
                  type="text" 
                  placeholder="Promo Code" 
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 px-5 text-sm outline-none focus:border-rose-400 transition-all placeholder:text-white/20"
                />
              <TbDiscount className="absolute right-4 top-1/2 -translate-y-1/2 text-rose-400" size={20} />
              </div>

              <div className="flex justify-between items-end mb-8">
                <span className="text-rose-200/60 font-bold uppercase tracking-widest text-xs">Total Amount</span>
                <span className="text-4xl font-black tracking-tighter">₹{total.toFixed(2)}</span>
              </div>

              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-rose-500 hover:bg-rose-400 text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-black/20 transition-colors flex items-center justify-center gap-3"
              >
                Checkout Now <HiOutlineArrowLeft className="rotate-180" />
              </motion.button>

              {/* Trust badges */}
              <div className="mt-8 pt-8 border-t border-white/10 flex justify-center gap-6 text-rose-200/40">
                <div className="flex flex-col items-center gap-1">
                  <RiShieldCheckLine size={24} />
                  <span className="text-[8px] font-bold uppercase">Secure</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <TbTruckDelivery size={24} />
                  <span className="text-[8px] font-bold uppercase">Discrete</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const EmptyCart = () => (
  <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center p-6">
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="text-center max-w-md"
    >
      <div className="w-32 h-32 bg-rose-100 rounded-[3rem] flex items-center justify-center mx-auto mb-8 text-rose-500">
        <RiShoppingBag3Line size={64} />
      </div>
      <h2 className="text-4xl font-black text-rose-950 tracking-tighter uppercase mb-4">Your box is empty</h2>
      <p className="text-rose-900/50 font-medium mb-10">
        It looks like you haven't added any essentials to your monthly care box yet.
      </p>
      <Link 
        to="/products" 
        className="inline-block bg-rose-500 text-white px-10 py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-rose-200 hover:bg-rose-600 transition-all"
      >
        Start Shopping
      </Link>
    </motion.div>
  </div>
);

export default Cart;